import { Injectable, Injector, computed, effect, inject, signal, untracked } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { AppModeService } from './app-mode.service';
import { IdiomaService } from './idioma.service';
import { textoPorDefecto } from '@shared/i18n/textos-ui';

/** `data` de `Idiomas/diccionario` (ApiService ya desempaqueta el sobre). */
interface DiccionarioApi {
   idioma?: string;
   idioma_origen?: string;
   idiomas?: string[];
   diccionario?: Record<string, unknown>;
}

/** Ámbito del back para los textos fijos del front. Las claves llegan como `ui.<clave>`. */
const AMBITO_UI = 'ui';

/**
 * Diccionario de textos fijos del front para el idioma efectivo (ver IdiomaService y
 * `shared/i18n/textos-ui.ts`).
 *
 * - Carga `Idiomas/diccionario` (público, sin token) con `ambito=ui` SOLO si el congreso es
 *   multiidioma y el idioma efectivo no es el origen. En cualquier otro caso no hay llamada y se
 *   usan los textos por defecto del código.
 * - Se precarga en el arranque (app-mode.initializer) para que la primera pantalla ya salga
 *   traducida, y se recarga sola cuando cambia el idioma (selector del usuario).
 * - Si falla la carga, la web sigue en el idioma origen: nunca se queda sin textos.
 */
@Injectable({
   providedIn: 'root'
})
export class DiccionarioService {
   /**
    * ApiService se resuelve al cargar, no al construir: así los componentes que usan el pipe `t`
    * no arrastran HttpClient a sus specs (sin congreso resuelto nunca se llama al back).
    */
   private readonly injector = inject(Injector);
   private readonly appMode = inject(AppModeService);
   private readonly idiomaService = inject(IdiomaService);

   private readonly entradas = signal<Readonly<Record<string, string>>>({});
   /** Idioma para el que está cargado `entradas` (null = nada cargado todavía). */
   private readonly idiomaCargado = signal<string | null>(null);

   readonly cargando = signal(false);
   /** Nº de claves traducidas disponibles para el idioma actual. */
   readonly total = computed(() => Object.keys(this.entradas()).length);

   constructor() {
      // Recarga cuando cambia el idioma efectivo. `cargando` evita duplicar la carga del arranque.
      effect(() => {
         const idioma = this.idiomaService.idioma();
         const ocupado = this.cargando();
         untracked(() => {
            if (!ocupado && idioma !== this.idiomaCargado()) {
               void firstValueFrom(this.cargar()).catch(() => undefined);
            }
         });
      });
   }

   /**
    * Texto de una clave en el idioma efectivo, o el texto por defecto del código.
    * Los marcadores `{nombre}` se sustituyen con `parametros`.
    */
   texto(clave: string, parametros?: Record<string, string | number>, porDefecto?: string): string {
      const base = this.entradas()[clave] ?? porDefecto ?? textoPorDefecto(clave);
      if (!parametros) {
         return base;
      }
      return base.replace(/\{(\w+)\}/g, (marcador, nombre: string) =>
         nombre in parametros ? String(parametros[nombre]) : marcador);
   }

   /** true si hay traducción cargada para la clave (no cuenta el texto por defecto). */
   tiene(clave: string): boolean {
      return clave in this.entradas();
   }

   /**
    * Carga (o vacía) el diccionario para el idioma efectivo actual. Idempotente: si ya está
    * cargado para ese idioma no vuelve a llamar al back.
    */
   cargar(): Observable<void> {
      const idioma = this.idiomaService.idioma();
      const sgiindice = this.appMode.congresoData()?.sgiindice;

      if (idioma !== null && idioma === this.idiomaCargado()) {
         return of(undefined);
      }
      if (!idioma || !sgiindice || !this.idiomaService.multiidioma() || idioma === this.idiomaService.origen()) {
         this.entradas.set({});
         this.idiomaCargado.set(idioma);
         return of(undefined);
      }

      this.cargando.set(true);
      return this.injector.get(ApiService).post<DiccionarioApi>('Idiomas/diccionario', { sgiindice, sgiidioma: idioma, ambito: AMBITO_UI }).pipe(
         map(respuesta => this.normalizar(respuesta?.diccionario)),
         catchError(() => of<Record<string, string>>({})),
         tap(entradas => {
            this.entradas.set(entradas);
            this.idiomaCargado.set(idioma);
         }),
         map(() => undefined),
         finalize(() => this.cargando.set(false)),
      );
   }

   /**
    * `ui.cesta.boton_pagar` → `cesta.boton_pagar`. Todo a string: ApiService convierte a número o
    * booleano los textos que lo parecen ("2026", "no") y aquí son siempre texto.
    */
   private normalizar(diccionario: Record<string, unknown> | undefined): Record<string, string> {
      const resultado: Record<string, string> = {};
      const prefijo = `${AMBITO_UI}.`;
      for (const [claveApi, valor] of Object.entries(diccionario ?? {})) {
         if (valor === null || valor === undefined) {
            continue;
         }
         const clave = claveApi.startsWith(prefijo) ? claveApi.slice(prefijo.length) : claveApi;
         const texto = valor instanceof Date ? valor.toISOString().slice(0, 10) : String(valor);
         if (texto !== '') {
            resultado[clave] = texto;
         }
      }
      return resultado;
   }
}
